import React, { useEffect, useState, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams } from "react-router-dom";
import CorporateUserDetailsComponent from "./component";

const CorporateUserDetailsContainer = (props) => {
  const { corporateId } = useParams();
  const navigate = useNavigate();

  const [BPList, setBPList] = useState([]);
  const [filteredBPList, setFilteredBPList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [editedDepartmentId, setEditedDepartmentId] = useState("");
  const [editedDepartmentName, setEditedDepartmentName] = useState("");
  const [currentStatusFilter, setCurrentStatusFilter] = useState("active");
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [corporateData, setCorporateData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const pageSize = 50;
  
  // Search debounce timer
  const searchTimerRef = useRef(null);
  const isLoadingRef = useRef(false);
  const scrollContainerRef = useRef(null);

  // Refresh function
  const refreshBPList = () => {
    setCurrentPage(0);
    setAllUsers([]);
    setHasMore(true);
    setSearchValue("");
    setIsSearchMode(false);
    handleGetCorporateDetails(0, false, "", "");
  };

  // 🔥 NORMAL FETCH: With pagination (limit & offset)
  const handleGetCorporateDetails = useCallback((page = 0, isLoadMore = false, userName = "", usersCorporateId = "") => {
    if (isLoadingRef.current) return;
    
    if (isLoadMore) {
      if (loadingMore) return;
      setLoadingMore(true);
    } else {
      setLoading(true);
      setInitialLoading(true);
    }
    
    isLoadingRef.current = true;

    let apiUrl = `/partner/api/v1/business-profile/${corporateId}`;
    const queryParams = [];
    
    // Add pagination params
    queryParams.push(`limit=${pageSize}`);
    queryParams.push(`offset=${page}`);
    
    if (queryParams.length > 0) {
      apiUrl += `?${queryParams.join('&')}`;
    }

    props.getDataFromAPI(
      apiUrl,
      "GET",
      undefined,
      (response) => {
        if (response && response) {
          setCorporateData(response);
          setTotalElements(response.totalElements || 0);
          
          if (response.bpUserInfoList && Array.isArray(response.bpUserInfoList)) {
            const users = response.bpUserInfoList;
            
            const hasMoreData = users.length === pageSize;
            setHasMore(hasMoreData);
            
            if (isLoadMore) {
              setAllUsers(prev => {
                const existingIds = new Set(prev.map(u => u.ucmId));
                const newUsers = users.filter(u => !existingIds.has(u.ucmId));
                return [...prev, ...newUsers];
              });
            } else {
              setAllUsers(users);
              setCurrentPage(page);
            }
            
            // Extract unique departments
            const allUsersList = isLoadMore ? [...allUsers, ...users] : users;
            const deptMap = new Map();
            allUsersList.forEach(user => {
              if (user.departmentId && user.departmentName && 
                  user.departmentName.toLowerCase() !== 'default' &&
                  user.departmentId !== 0) {
                if (!deptMap.has(user.departmentId)) {
                  deptMap.set(user.departmentId, {
                    id: user.departmentId,
                    name: user.departmentName
                  });
                }
              }
            });
            setDepartments(Array.from(deptMap.values()));
          } else {
            if (!isLoadMore) {
              setAllUsers([]);
              setDepartments([]);
              setHasMore(false);
            }
          }
        } else {
          if (!isLoadMore) {
            setCorporateData(null);
            setAllUsers([]);
            setDepartments([]);
            setHasMore(false);
          }
        }
        
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
          setInitialLoading(false);
        }
        isLoadingRef.current = false;
      },
      (err) => {
        console.error("API Error:", err);
        toastr.error("Failed", err?.message || "Unable to fetch corporate details.");
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
          setInitialLoading(false);
        }
        isLoadingRef.current = false;
      }
    );
  }, [corporateId, pageSize, props]);

  // 🔥 SEARCH API CALL: Without pagination (only search params)
  const handleSearchAPI = useCallback((searchTerm, type) => {
    if (isLoadingRef.current) return;
    
    setLoading(true);
    isLoadingRef.current = true;

    let apiUrl = `/partner/api/v1/business-profile/${corporateId}`;
    const queryParams = [];
    
    // Add search param based on type
    if (type === 'name' && searchTerm) {
      queryParams.push(`userName=${encodeURIComponent(searchTerm)}`);
    } else if (type === 'id' && searchTerm) {
      queryParams.push(`usersCorporateId=${encodeURIComponent(searchTerm)}`);
    }
    
    if (queryParams.length > 0) {
      apiUrl += `?${queryParams.join('&')}`;
    }

    props.getDataFromAPI(
      apiUrl,
      "GET",
      undefined,
      (response) => {
        if (response && response.bpUserInfoList && Array.isArray(response.bpUserInfoList)) {
          const users = response.bpUserInfoList;
          setAllUsers(users);
          setFilteredBPList(users);
          setHasMore(false);
          setIsSearchMode(true);
          setTotalElements(users.length);
          
          if (response.corporateName) {
            setCorporateData(response);
          }
          
          const deptMap = new Map();
          users.forEach(user => {
            if (user.departmentId && user.departmentName && 
                user.departmentName.toLowerCase() !== 'default' &&
                user.departmentId !== 0) {
              if (!deptMap.has(user.departmentId)) {
                deptMap.set(user.departmentId, {
                  id: user.departmentId,
                  name: user.departmentName
                });
              }
            }
          });
          setDepartments(Array.from(deptMap.values()));
        } else {
          setAllUsers([]);
          setFilteredBPList([]);
          setTotalElements(0);
        }
        
        setLoading(false);
        isLoadingRef.current = false;
      },
      (err) => {
        console.error("Search API Error:", err);
        toastr.error("Failed", err?.message || "Unable to search users.");
        setLoading(false);
        isLoadingRef.current = false;
      }
    );
  }, [corporateId, props]);

  // Intelligent search
  const handleSearchWithApi = useCallback((searchTerm) => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    
    const trimmedTerm = searchTerm?.trim() || "";
    
    if (!trimmedTerm) {
      setIsSearchMode(false);
      setCurrentPage(0);
      setAllUsers([]);
      setHasMore(true);
      handleGetCorporateDetails(0, false, "", "");
      return;
    }
    
    searchTimerRef.current = setTimeout(() => {
      const isNumericId = /^\d+$/.test(trimmedTerm);
      
      if (isNumericId) {
        handleSearchAPI(trimmedTerm, 'id');
      } else {
        handleSearchAPI(trimmedTerm, 'name');
      }
    }, 500);
  }, [handleSearchAPI, handleGetCorporateDetails]);

  // Initial load
  useEffect(() => {
    if (corporateId) {
      handleGetCorporateDetails(0, false, "", "");
    } else {
      toastr.error("Error", "Invalid corporate ID");
      setInitialLoading(false);
    }
    
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [corporateId]);

  // Filter users locally when status filter changes
  useEffect(() => {
    let filtered = [...allUsers];
    
    if (currentStatusFilter === "active") {
      filtered = filtered.filter((user) => user.status === true);
    } else if (currentStatusFilter === "inactive") {
      filtered = filtered.filter((user) => user.status === false);
    }
    
    setFilteredBPList(filtered);
    setSelectedRows([]);
  }, [allUsers, currentStatusFilter]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    handleSearchWithApi(value);
  };

  const handleStatusToggle = (status) => {
    setCurrentStatusFilter(status);
  };

  const handleRowSelection = (index) => {
    setSelectedRows(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredBPList.map((_, i) => i));
    } else {
      setSelectedRows([]);
    }
  };

  const getSelectedEmployeeIds = () => {
    return selectedRows
      .map((index) => filteredBPList[index]?.usersCorporateId)
      .filter(Boolean);
  };

  const getSelectedUcmIds = () => {
    return selectedRows
      .map((index) => filteredBPList[index]?.ucmId)
      .filter(Boolean);
  };

  // ✅ Single User Activate - FIXED
  const handleActivateClick = (index) => {
    setCurrentRowIndex(index);
    setActivateOpen(true);
  };

  const handleConfirmActivate = () => {
    if (currentRowIndex === null) return;
    
    const userToActivate = filteredBPList[currentRowIndex];
    const employeeId = userToActivate?.usersCorporateId;
    
    if (!employeeId) {
      toastr.error("Error", "No employee ID found to activate.");
      setActivateOpen(false);
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}/activate`,
      "POST",
      { employeeIds: [employeeId] },
      () => {
        toastr.success("Success", "User activated successfully.");
        setActivateOpen(false);
        refreshBPList();
        setSelectedRows([]);
        setLoading(false);
      },
      (err) => {
        const errorMsg = err?.message || "Unable to activate user.";
        toastr.error("Failed", errorMsg);
        setActivateOpen(false);
        setLoading(false);
      }
    );
  };

  // ✅ Single User Deactivate - FIXED
  const handleDeactivateClick = (index) => {
    setCurrentRowIndex(index);
    setDeactivateOpen(true);
  };

  const handleConfirmDeactivate = () => {
    if (currentRowIndex === null) return;
    
    const userToDeactivate = filteredBPList[currentRowIndex];
    const employeeId = userToDeactivate?.usersCorporateId;
    
    if (!employeeId) {
      toastr.error("Error", "No employee ID found to deactivate.");
      setDeactivateOpen(false);
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}`,
      "DELETE",
      { employeeIds: [employeeId] },
      () => {
        toastr.success("Success", "User deactivated successfully.");
        setDeactivateOpen(false);
        refreshBPList();
        setSelectedRows([]);
        setLoading(false);
      },
      (err) => {
        const errorMsg = err?.message || "Unable to deactivate user.";
        toastr.error("Failed", errorMsg);
        setDeactivateOpen(false);
        setLoading(false);
      }
    );
  };

  // ✅ Bulk Activate - FIXED
  const handleBulkActivate = () => {
    const selectedEmployeeIds = getSelectedEmployeeIds();
    
    if (selectedEmployeeIds.length === 0) {
      toastr.error("Error", "No users selected for activation.");
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}/activate`,
      "POST",
      { employeeIds: selectedEmployeeIds },
      () => {
        toastr.success("Success", `${selectedEmployeeIds.length} user(s) activated successfully.`);
        setSelectedRows([]);
        refreshBPList();
        setLoading(false);
      },
      (err) => {
        const errorMsg = err?.message || "Unable to activate users.";
        toastr.error("Failed", errorMsg);
        setLoading(false);
      }
    );
  };

  // ✅ Bulk Deactivate - FIXED
  const handleBulkDeactivate = () => {
    const selectedEmployeeIds = getSelectedEmployeeIds();
    
    if (selectedEmployeeIds.length === 0) {
      toastr.error("Error", "No users selected for deactivation.");
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}`,
      "DELETE",
      { employeeIds: selectedEmployeeIds },
      () => {
        toastr.success("Success", `${selectedEmployeeIds.length} user(s) deactivated successfully.`);
        setSelectedRows([]);
        refreshBPList();
        setLoading(false);
      },
      (err) => {
        const errorMsg = err?.message || "Unable to deactivate users.";
        toastr.error("Failed", errorMsg);
        setLoading(false);
      }
    );
  };

  const handleEditClick = (index) => {
    setCurrentRowIndex(index);
    const user = filteredBPList[index];
    setEditedName(user.userName || '');
    setEditedDepartmentId(user.departmentId || '');
    setEditedDepartmentName(user.departmentName || '');
    setEditOpen(true);
  };

  const handleSaveEdit = () => {
    if (currentRowIndex === null) return;
    const userToUpdate = filteredBPList[currentRowIndex];
    handleUpdateUser(userToUpdate.ucmId);
  };

  const handleUpdateUser = (ucmId) => {
    if (!ucmId) {
      toastr.error("Error", "No user ID provided");
      return;
    }
    
    if (!editedDepartmentId) {
      toastr.error("Error", "Please select a department");
      return;
    }
    
    setLoading(true);
    
    const selectedDept = departments.find(d => d.id == editedDepartmentId);
    const deptName = selectedDept?.name || "";
    
    const payload = {
      userName: editedName,
      departmentId: parseInt(editedDepartmentId),
      departmentName: deptName
    };
    
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${ucmId}`,
      "PATCH",
      payload,
      () => {
        toastr.success("Success", "User updated successfully");
        setEditOpen(false);
        refreshBPList();
        setSelectedRows([]);
        setEditedDepartmentId("");
        setEditedDepartmentName("");
        setLoading(false);
      },
      (err) => {
        const errorMsg = err.message || "Something went wrong.";
        toastr.error("Error", errorMsg);
        setLoading(false);
      }
    );
  };

  const handleUnlinkClick = (index) => {
    setCurrentRowIndex(index);
    setUnlinkOpen(true);
  };

  const handleConfirmUnlink = () => {
    const userToUnlink = filteredBPList[currentRowIndex];
    const ucmId = userToUnlink?.ucmId;
    if (!ucmId) {
      toastr.error("Error", "No employee ID found to unlink.");
      return;
    }
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/approve/${ucmId}`,
      "POST",
      undefined,
      () => {
        toastr.success("Success", "User unlinked successfully.");
        setUnlinkOpen(false);
        refreshBPList();
        setSelectedRows([]);
        setLoading(false);
      },
      (err) => {
        const errorMsg = err?.status?.message || "Something went wrong.";
        toastr.error("Error", errorMsg);
        setLoading(false);
      }
    );
  };

  const handleDeleteClick = (index) => {
    setCurrentRowIndex(index);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (currentRowIndex === null) return;
    
    const userToDelete = filteredBPList[currentRowIndex];
    const ucmId = userToDelete?.ucmId;
    const departmentId = userToDelete?.departmentId;
    
    if (!ucmId) {
      toastr.error("Error", "No user ID found to delete.");
      setDeleteOpen(false);
      return;
    }
    
    const payload = {
      employeeIds: [ucmId],
      departmentId: departmentId
    };
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}`,
      "DELETE",
      payload,
      () => {
        toastr.success("Success", "User deleted successfully.");
        setDeleteOpen(false);
        refreshBPList();
        setSelectedRows([]);
        setLoading(false);
      },
      (err) => {
        const errorMsg = err?.message || "Unable to delete user.";
        toastr.error("Failed", errorMsg);
        setDeleteOpen(false);
        setLoading(false);
      }
    );
  };

  const handleLoadMore = () => {
    if (!isSearchMode && !loadingMore && hasMore && !isLoadingRef.current) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      handleGetCorporateDetails(nextPage, true, "", "");
    }
  };

  const handleAddNewCorporateUser = () => {
    navigate(`/home/customermanager/bpmanagementlist/corporatemanagementusercreation/${corporateId}`);
  };

  return (
    <CorporateUserDetailsComponent
      corporateData={corporateData}
      BPList={filteredBPList}
      selectedRows={selectedRows}
      handleRowSelection={handleRowSelection}
      handleSelectAll={handleSelectAll}
      editOpen={editOpen}
      setEditOpen={setEditOpen}
      editedName={editedName}
      setEditedName={setEditedName}
      editedDepartmentId={editedDepartmentId}
      setEditedDepartmentId={setEditedDepartmentId}
      editedDepartmentName={editedDepartmentName}
      setEditedDepartmentName={setEditedDepartmentName}
      handleEditClick={handleEditClick}
      handleSaveEdit={handleSaveEdit}
      unlinkOpen={unlinkOpen}
      setUnlinkOpen={setUnlinkOpen}
      handleUnlinkClick={handleUnlinkClick}
      handleConfirmUnlink={handleConfirmUnlink}
      searchValue={searchValue}
      handleSearchChange={handleSearchChange}
      handleStatusToggle={handleStatusToggle}
      currentStatusFilter={currentStatusFilter}
      handleAddNewCorporateUser={handleAddNewCorporateUser}
      loading={loading || initialLoading}
      loadingMore={loadingMore}
      hasMore={hasMore}
      totalElements={totalElements}
      onLoadMore={handleLoadMore}
      departments={departments}
      deleteOpen={deleteOpen}
      setDeleteOpen={setDeleteOpen}
      handleDeleteClick={handleDeleteClick}
      handleConfirmDelete={handleConfirmDelete}
      activateOpen={activateOpen}
      setActivateOpen={setActivateOpen}
      handleActivateClick={handleActivateClick}
      handleConfirmActivate={handleConfirmActivate}
      deactivateOpen={deactivateOpen}
      setDeactivateOpen={setDeactivateOpen}
      handleDeactivateClick={handleDeactivateClick}
      handleConfirmDeactivate={handleConfirmDeactivate}
      scrollContainerRef={scrollContainerRef}
      handleBulkActivate={handleBulkActivate}
      handleBulkDeactivate={handleBulkDeactivate}
    />
  );
};

function mapStateToProps(state) {
  return {
    auth: state.auth,
  };
}

export default connect(mapStateToProps, { getDataFromAPI })(CorporateUserDetailsContainer);